<script setup lang="ts">
import {
  useUpdateUserVariableDefinitionValueSelectOptionMutation,
  useRemoveUserVariableDefinitionValueSelectOptionMutation,
  type SettingsPageLineActivationUserVariableDefinitionValueSelectOptionEditRowOptionFragment,
} from './SettingsPageLineActivationUserVariableDefinitionValueSelectOptionEditRow.generated'
import FlatButton from '@/components/atoms/FlatButton.vue'
import IconPen from '@/components/icons/IconPen.vue'
import IconDustBox from '@/components/icons/IconDustBox.vue'
import { ref } from 'vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import { useAlert } from '@/libs/alertPortal'

const props = defineProps<{
  userVariableDefinitionGuid: string
  option: SettingsPageLineActivationUserVariableDefinitionValueSelectOptionEditRowOptionFragment
  optionLength: number
}>()

const emit = defineEmits<{
  (event: 'changed'): void
}>()

const alert = useAlert()

const name = ref(props.option.name)

type Mode = 'view' | 'edit'

const mode = ref<Mode>('view')

const setMode = (m: Mode) => {
  mode.value = m
}

const updateMutation =
  useUpdateUserVariableDefinitionValueSelectOptionMutation()
const removeMutation =
  useRemoveUserVariableDefinitionValueSelectOptionMutation()

const handleClickUpdateButton = async () => {
  if (name.value.length === 0) {
    return
  }

  if (name.value === props.option.name) {
    setMode('view')
    return
  }

  await updateMutation.mutate({
    input: {
      userVariableDefinitionGUID: props.userVariableDefinitionGuid,
      selectOptionGUID: props.option.guid,
      name: name.value,
    },
  })

  emit('changed')

  alert.success('選択肢を更新しました')

  setMode('view')
}

const handleClickDeleteButton = async () => {
  if (props.optionLength <= 2) {
    alert.error('選択肢は2つ以上必要です')
    return
  }

  const confirmed = window.confirm('選択肢を削除しますか？')
  if (!confirmed) {
    return
  }

  const res = await removeMutation.mutate({
    input: {
      userVariableDefinitionGUID: props.userVariableDefinitionGuid,
      selectOptionGUID: props.option.guid,
    },
  })

  if (
    res?.data?.removeUserVariableDefinitionValueSelectOption.__typename ===
    'RemoveUserVariableDefinitionValueSelectOptionSuccessPayload'
  ) {
    emit('changed')
    alert.success('選択肢を削除しました')
    return
  } else if (
    res?.data?.removeUserVariableDefinitionValueSelectOption.__typename ===
      'RemoveUserVariableDefinitionValueSelectOptionErrorPayload' &&
    res.data.removeUserVariableDefinitionValueSelectOption.error ===
      'ALREADY_USED'
  ) {
    alert.error('すでに使用されているため、削除できません')
    return
  } else {
    alert.error('選択肢の削除に失敗しました')
  }
}

const handleClickCancelEditButton = () => {
  setMode('view')
  name.value = props.option.name
}
</script>

<template>
  <div :class="$style.row">
    <template v-if="mode === 'view'">
      <p :class="$style.name">{{ option.name }}</p>
      <FlatButton variant="base" :class="$style.button" @click="setMode('edit')"
        ><IconPen
      /></FlatButton>
      <FlatButton
        variant="base"
        :class="$style.button"
        @click="handleClickDeleteButton"
        ><IconDustBox
      /></FlatButton>
    </template>
    <template v-if="mode === 'edit'">
      <DolphinTextInput v-model="name" :class="$style.nameInput" />
      <FlatButton
        @click="handleClickUpdateButton"
        variant="primary"
        :class="$style.button"
        >保存</FlatButton
      >
      <FlatButton
        @click="handleClickCancelEditButton"
        variant="base"
        :class="$style.button"
        >キャンセル</FlatButton
      >
    </template>
  </div>
</template>

<style lang="scss" module>
.row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.name {
  flex: 1;
  font-size: 16px;
}

.nameInput {
  flex: 1;
  height: 39px;
}

.button {
  padding: 8px 16px;
}
</style>
